import { ConfigurationFilters } from '@reliance/types/filters/ConfigurationFilters';
import { Configuration } from '@reliance/types/models/Configuration';
import { AxiosError } from 'axios';
import { FORM_ERROR } from 'final-form';
import { useAsync, type UseAsyncReturn } from 'react-async-hook';
import api from '../common/api';
import { IStatusResponse } from '../common/reliance-api-service';

/**
 * Saves the given (multiple) configuration keys/values to the facility configurations
 *
 * @param facilityId - The ID of the facility to save the configurations for
 * @param configurationKeys - The configuration keys to save
 * @param existingConfigurations - The existing configurations to update
 * @param values - The values to save
 * @returns An object with the FORM_ERROR key if there is an error, or undefined if successful
 */
export async function saveFacilityConfigurations(
  facilityId: number,
  configurationKeys: Array<{ key: string }>,
  existingConfigurations: Configuration[],
  values: Record<string, string>
): Promise<{ [FORM_ERROR]: string } | void> {
  try {
    for (const configPair of configurationKeys) {
      const value = values[configPair.key] ?? '';
      const existing = existingConfigurations.find((configuration) => configuration.key === configPair.key);
      if (existing != null && existing.id != null) {
        await api.put<IStatusResponse>(`/configuration/${existing.id}`, { value });
      } else {
        await api.post<IStatusResponse>('/configuration', {
          key: configPair.key,
          value,
          facilityId,
        });
      }
    }
  } catch (err) {
    const axiosError = err as AxiosError;
    if (axiosError.response?.status === 400) {
      return { [FORM_ERROR]: 'Please verify the inputs are valid and try again' };
    }
    if (axiosError.response?.status === 409) {
      return { [FORM_ERROR]: 'A configuration with this key already exists' };
    }
    return { [FORM_ERROR]: 'An unexpected error occurred' };
  }
  return undefined;
}

export function useFacilityConfigurations(
  facilityId: number | undefined
): UseAsyncReturn<Configuration[], [number | undefined]> {
  return useAsync<Configuration[], [number | undefined]>(
    async () => {
      if (facilityId == null) {
        return [];
      }
      const params: ConfigurationFilters = { facilityId };
      const { data } = await api.get<Configuration[] | null>(`/configuration`, { params });
      if (data == null) {
        return [];
      }
      return Array.isArray(data) ? data : [];
    },
    [facilityId]
  );
}
